import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { Icon } from '@/components/icons';
import styles from './platform-ui.module.css';

/**
 * The gate in front of every action that removes someone's access.
 *
 * Three things have to be true before Confirm becomes usable, and none of them
 * is decoration:
 *
 *  1. The impact summary has been fetched and shown. Nobody can judge whether to
 *     suspend a university from a button label; the server is the only thing that
 *     knows how many people and products sit behind that id. If the summary
 *     cannot be fetched the operator has to tick an acknowledgement saying they
 *     are proceeding blind — the action is still possible, because an outage in
 *     a reporting endpoint must not block an emergency suspension, but it is no
 *     longer accidental.
 *  2. A reason of real length has been typed. It is written into the audit trail
 *     and read months later by someone reconstructing what happened.
 *  3. For the destructive tier, the institution or product code has been typed
 *     out. This is the step that makes a misclick impossible.
 *
 * The requirement is that a high-risk action is never one click. This is that
 * requirement expressed as a component so no screen can forget it.
 */

const MINIMUM_REASON_LENGTH = 10;

export type ImpactStatus = 'loading' | 'ready' | 'unavailable';

interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  /** One sentence saying plainly what is about to happen. */
  intro: string;
  tone: 'critical' | 'warning' | 'neutral';
  /** The rendered impact summary. Only shown when impactStatus is 'ready'. */
  impact?: ReactNode;
  impactStatus: ImpactStatus;
  /** Action-specific inputs — a suspension scope, a trial flag, an end date. */
  extraFields?: ReactNode;
  /** Whether those extra inputs are complete. Blocks Confirm while false. */
  extraValid?: boolean;
  /** When set, must be typed exactly before the action can be confirmed. */
  confirmPhrase?: string;
  confirmLabel: string;
  submitting: boolean;
  /** A failure from the action itself, shown without closing the dialog. */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function ConfirmActionDialog({
  open,
  title,
  intro,
  tone,
  impact,
  impactStatus,
  extraFields,
  extraValid = true,
  confirmPhrase,
  confirmLabel,
  submitting,
  error,
  onCancel,
  onConfirm,
}: ConfirmActionDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [phrase, setPhrase] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const reasonId = useId();
  const phraseId = useId();

  // Reopening for a different institution must not inherit the last reason —
  // that is how the wrong justification ends up in an audit record.
  useEffect(() => {
    if (open) {
      setReason('');
      setPhrase('');
      setAcknowledged(false);
    }
  }, [open]);

  const reasonValid = reason.trim().length >= MINIMUM_REASON_LENGTH;
  const phraseValid = !confirmPhrase || phrase.trim() === confirmPhrase;
  const impactSettled = impactStatus === 'ready' || (impactStatus === 'unavailable' && acknowledged);
  const canConfirm = reasonValid && phraseValid && impactSettled && extraValid && !submitting;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={intro}
      // A stray click outside must not discard a half-written justification.
      closeOnBackdropClick={false}
    >
      <div className={`${styles.confirm} ${styles[`confirm_${tone}`]}`}>
        {impactStatus === 'loading' && (
          <p className={styles.confirmImpactLoading} role="status" aria-live="polite">
            {t('platform.confirm.impactLoading')}
          </p>
        )}

        {impactStatus === 'ready' && (
          <div className={styles.confirmImpact}>
            <h3 className={styles.confirmImpactTitle}>
              <Icon name="warning" size={16} aria-hidden="true" />
              {t('platform.confirm.impactTitle')}
            </h3>
            {impact}
          </div>
        )}

        {impactStatus === 'unavailable' && (
          <div className={styles.confirmImpactMissing}>
            <p className={styles.confirmImpactMissingText}>{t('platform.confirm.impactUnavailable')}</p>
            <Checkbox
              label={t('platform.confirm.impactAcknowledge')}
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
          </div>
        )}

        {extraFields}

        <div className={styles.confirmField}>
          <label className={styles.confirmLabel} htmlFor={reasonId}>
            {t('platform.confirm.reasonLabel')}
          </label>
          <p className={styles.confirmHint} id={`${reasonId}-hint`}>
            {/* Not named `count`: i18next reads that key as a plural selector
                and would demand _one/_other variants for a plain number. */}
            {t('platform.confirm.reasonHint', { min: MINIMUM_REASON_LENGTH })}
          </p>
          <textarea
            id={reasonId}
            className={styles.confirmTextarea}
            rows={3}
            value={reason}
            required
            aria-describedby={`${reasonId}-hint`}
            aria-invalid={reason.length > 0 && !reasonValid}
            onChange={(event) => setReason(event.target.value)}
            disabled={submitting}
          />
        </div>

        {confirmPhrase && (
          <div className={styles.confirmField}>
            <label className={styles.confirmLabel} htmlFor={phraseId}>
              {t('platform.confirm.phraseLabel', { phrase: confirmPhrase })}
            </label>
            <input
              id={phraseId}
              className={styles.confirmInput}
              value={phrase}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={phrase.length > 0 && !phraseValid}
              onChange={(event) => setPhrase(event.target.value)}
              disabled={submitting}
            />
          </div>
        )}

        {error && (
          <p className={styles.confirmError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'critical' ? 'modal-button modal-button--danger' : 'modal-button'}
            disabled={!canConfirm}
            onClick={() => onConfirm(reason.trim())}
          >
            {submitting ? t('platform.confirm.submitting') : confirmLabel}
          </button>
        </div>

        {/* Announced rather than left to be inferred from a greyed-out button. */}
        <p className={styles.confirmStatus} role="status" aria-live="polite">
          {canConfirm ? t('platform.confirm.ready') : t('platform.confirm.blocked')}
        </p>
      </div>
    </Modal>
  );
}
